import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";

import { supabase } from "./supabase";

/** Who is signed in, and whether we know yet. `loading` is a real third
 *  state: treating "not yet known" as "signed out" flashes the login screen
 *  at somebody who is already signed in. */
type AuthState = {
  session: Session | null;
  loading: boolean;
  /** They followed a password-reset link and must choose a new password. */
  recovering: boolean;
  recoveryDone: () => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  session: null,
  loading: true,
  recovering: false,
  recoveryDone: () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === "PASSWORD_RECOVERY") setRecovering(true);
      setSession(next);
      setLoading(false);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      loading,
      recovering,
      recoveryDone: () => setRecovering(false),
      signOut: async () => void (await supabase.auth.signOut()),
    }),
    [session, loading, recovering],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
